<template>
    <RbacDisableGuard
        v-slot="{ isDisabledByRbac }"
        :features="contractExtractionRequiredFeatures"
    >
        <div class="ContractExtractionReview">
            <LogoLoader v-if="isLoading" />
            <template v-else>
                <DocumentActionHeader
                    ref="actionHeader"
                    title="Contract Extraction"
                    :subtitle="CONTRACT_EXTRACTION_SUBTITLE"
                    :actions-disabled="isDisabledByRbac"
                    primary-action-label="Accept & Assign"
                    secondary-action-label="Decline Contract"
                    @back="onCancelClick"
                    @secondary-action="onRejectClick"
                    @primary-action="onAcceptClick"
                />

                <DocumentActionSplitLayout
                    splitter-storage-key="contract-extraction-splitter-position"
                    :right-panel-min-height="requirementsMinHeight"
                >
                    <template #before>
                        <TrackedActionReviewDocuments
                            :documents="displayedDocuments"
                            :storage-id="actionDetails.contractExtractionId"
                            :anchor-target="documentsAnchorTarget"
                            :page-top-offset="pageTopOffset"
                            :anchor-gap="documentsAnchorGap"
                            @position-change="documentsFixedTop = $event"
                        />
                    </template>

                    <template #after>
                        <ContractExtractionReviewPanel
                            :can-view-contract-extraction-data="canViewContractExtractionData"
                            :rp-name="rpName"
                            :contract-extraction-id="actionDetails.contractExtractionId"
                            :criteria-status="contractExtractionCriteriaStatus"
                            :requirement-types="contractExtractionRequirementTypes"
                            :requirement-models="contractExtractionRequirementModels"
                            :criterion-templates="contractExtractionCriterionTemplates"
                            :custom-properties="contractExtractionCustomProperties"
                            :country-code="contractExtractionCountryCode"
                            :can-manage-requirements="canManageContractExtractionRequirements"
                            :entity-requirement-types="nonMultiInstanceRequirementTypes"
                            :requirement-definitions="requirementDefinitions"
                            :entity-form-has-errors="entityFormHasErrors"
                            :entity-search-has-error="entitySearchHasError"
                            :requirements-form-has-errors="requirementsFormHasErrors"
                            :requirements-show-validation-errors="submitAttempted"
                            @entity-select="onEntitySelect"
                            @entity-mode-change="onEntityModeChange"
                            @requirements-has-errors-change="requirementsHasInvalidReferences = $event"
                        />
                    </template>
                </DocumentActionSplitLayout>

                <ActionReviewRejectionModal
                    v-if="showRejectConfirmation"
                    :title="rejectionModalProps.title"
                    :message="rejectionModalProps.message"
                    :confirm-button-label="rejectionModalProps.confirmButtonLabel"
                    @close="onRejectConfirmation"
                    @input="onRejectConfirmation"
                />
            </template>
        </div>
    </RbacDisableGuard>
</template>

<style lang="scss">
.ContractExtractionReview {
    display: flex;
    flex-direction: column;
    background: var(--eidBgGrayTones-lightest);

    .LogoLoader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 0.7em;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { Vue } from '@evidentid/vue-property-decorator';

    import LogoLoader from '@evidentid/dashboard-commons/components/LogoLoader/LogoLoader.vue';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';
    import RbacDisableGuard from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableGuard.vue';
    import { typeNarrowing } from '@evidentid/dashboard-commons/utils/typeNarrowing';
    import type { TprmRequirementDefinition } from '@evidentid/tprm-portal-lib/models/common';
    import type { CustomProperty, EntityInput } from '@evidentid/tprm-portal-lib/models/dashboard';
    import type { CriterionField, CriterionInput, CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import type { ContractExtractionActionDetails } from '@evidentid/tprm-portal-lib/models/entity-actions-review/actions/contract-extraction/ContractExtractionActionDetails.model';
    import type { ContractExtractionResolveInput } from '@evidentid/tprm-portal-lib/models/entity-actions-review/actions/contract-extraction/ContractExtractionResolveInput.model';
    import { ActionReview } from '@evidentid/tprm-portal-lib/models/entity-actions-review/commons/ActionReview.model';
    import { ActionType } from '@evidentid/tprm-portal-lib/models/entity-actions-review/commons/ActionType.model';
    import type { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import type {
        TprmRequirementModel,
    } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import TrackedActionReviewDocuments
        from '@/components/action-review-documents/TrackedActionReviewDocuments.vue';
    import ContractExtractionReviewPanel
        from '@/modules/contract-extraction/components/ContractExtractionReviewPanel/ContractExtractionReviewPanel.vue';
    import {
        type ContractExtractionCriteriaStatus,
        type ContractExtractionDocumentsStatus,
        createEmptyContractExtractionCriteriaStatus,
        useContractExtractionStore,
    } from '@/modules/contract-extraction/store';
    import { buildEntityInputFromFormValues } from '@/modules/contract-extraction/utils/buildEntityInputFromFormValues';
    import { convertContractEntityToApiModel } from '@/modules/contract-extraction/utils/convertContractEntityToApiModel';
    import { createEmptyCustomPropertyStatus, useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import { EditableRequirementCriterion } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import {
        type CriterionTemplatesStatus,
        type RequirementModelsStatus,
        type RequirementTypesPerCountryStatus,
        createEmptyCriterionTemplatesStatus,
        createEmptyRequirementModelsStatus,
        createEmptyRequirementTypesPerCountry,
        useDecisioningCriteriaStore,
    } from '@/modules/decisioning-criteria/store';
    import {
        editableRequirementCriterionToRiskProfileCriterion,
    } from '@/modules/decisioning-criteria/utils/requirementsEditorConverters';
    import { useDynamicFormStore } from '@/modules/dynamic-form/store';
    import ActionReviewRejectionModal
        from '@/modules/entity-actions-review/components/ActionReviewRejectionModal/ActionReviewRejectionModal.vue';
    import DocumentActionHeader
        from '@/modules/entity-actions-review/components/DocumentActionHeader/DocumentActionHeader.vue';
    import DocumentActionSplitLayout
        from '@/modules/entity-actions-review/components/DocumentActionSplitLayout/DocumentActionSplitLayout.vue';
    import {
        filterEntityEmptyItems,
        trimEntity,
    } from '@/modules/entity-management/utils/entityProcessing';

    function createEmptyContractExtractionDocumentsStatus(): ContractExtractionDocumentsStatus {
        return {
            status: OperationStatus.uninitialized,
            documents: null,
        };
    }

    const PAGE_TOP_OFFSET_PX = 15 + 92;
    const DOCUMENTS_ANCHOR_GAP_PX = 21;
    const CONTRACT_EXTRACTION_COUNTRY_CODE = 'US';
    const CONTRACT_EXTRACTION_SUBTITLE =
        'Review the extracted criteria and fill in any missing information. ' +
        'AI may make mistakes, so please review the results carefully.';

    export default defineComponent({
        name: 'ContractExtractionReview',
        components: {
            ActionReviewRejectionModal,
            ContractExtractionReviewPanel,
            DocumentActionHeader,
            DocumentActionSplitLayout,
            LogoLoader,
            RbacDisableGuard,
            TrackedActionReviewDocuments,
        },
        props: {
            actionReview: {
                type: Object as PropType<ActionReview>,
                required: true,
            },
        },
        // PRODUCT-26110 Task 13: this component's template root is RbacDisableGuard,
        // whose render() always returns a Fragment (see RbacDisableGuard.vue's comment).
        // ActionReviewDetails.vue's `<component :is="reviewComp" @resolve=... @cancel=...>`
        // binds both listeners on every COMPONENT_MAP entry, including this one; without a
        // declared `emits`, Vue 3 treats them as extraneous and warns when it can't forward
        // them through the Fragment root -- declaring them here marks them consumed at this
        // hop instead.
        emits: [ 'resolve', 'cancel' ],
        data() {
            return {
                CONTRACT_EXTRACTION_SUBTITLE,
                documentsAnchorTarget: null as HTMLElement | null,
                documentsFixedTop: PAGE_TOP_OFFSET_PX,
                RbacFeature,
                submitAttempted: false,
                requirementsHasInvalidReferences: false,
                showRejectConfirmation: false,
                selectedEntityId: null as string | null,
                isAssignToExisting: false,
                loadDocumentPromise: null as Promise<void> | null,
                // PRODUCT-26110 Phase 2B2: Promise.all([...6 store actions]).finally(...)
                // resolves to the 6-tuple of the actions' return values, not void -- widen to
                // Promise<any> (vs. loadDocumentPromise's single Promise.resolve(...).finally(...),
                // which really does resolve void) rather than narrowing the source expression.
                loadDataPromise: null as Promise<any> | null,
                rejectionModalProps: {
                    title: 'Decline Contract',
                    message: 'Declining this contract will delete any extracted requirements, requirement changes, new entity creation, or assignment rules. The assigned entity will not be notified or assigned to this risk profile, and the risk profile will be removed.',
                    confirmButtonLabel: 'Decline Contract',
                },
            };
        },
        computed: {
            actionDetails(): ContractExtractionActionDetails {
                return typeNarrowing<ContractExtractionActionDetails>(
                    (this as any).actionReview.action,
                    (this as any).actionReview.action.$action === ActionType.contractExtraction,
                    'The actionReview object of this component has incorrect data, this is likely caused by mistake in the code.',
                );
            },
            rpName(): string {
                return (this as any).$rp?.current || '';
            },
            contractExtractionDocumentsStatus(): ContractExtractionDocumentsStatus {
                const rpDocuments = useContractExtractionStore().documents[(this as any).rpName] || {};
                return rpDocuments[(this as any).actionDetails.contractExtractionId]
                    || createEmptyContractExtractionDocumentsStatus();
            },
            contractExtractionCriteriaStatus(): ContractExtractionCriteriaStatus {
                const rpCriteria = useContractExtractionStore().criteria[(this as any).rpName] || {};
                return rpCriteria[(this as any).actionDetails.contractExtractionId]
                    || createEmptyContractExtractionCriteriaStatus();
            },
            criterionTemplatesStatus(): CriterionTemplatesStatus {
                return useDecisioningCriteriaStore().criterionTemplates[(this as any).rpName]
                    || createEmptyCriterionTemplatesStatus();
            },
            requirementTypesPerCountryStatus(): RequirementTypesPerCountryStatus {
                return useDecisioningCriteriaStore().requirementTypesPerCountry[(this as any).rpName]
                    || createEmptyRequirementTypesPerCountry();
            },
            requirementModelsStatus(): RequirementModelsStatus {
                return useDecisioningCriteriaStore().requirementModelsStatus[(this as any).rpName]
                    || createEmptyRequirementModelsStatus();
            },
            customPropertiesStatus(): { status: OperationStatus, list: unknown[] } {
                return useDashboardStore().customProperties[(this as any).rpName]
                    || createEmptyCustomPropertyStatus();
            },
            clientRequirementTypesStatus(): { status: OperationStatus, list: string[] } {
                return useDashboardStore().clientRequirementTypes[(this as any).rpName]
                    || { status: OperationStatus.uninitialized, list: [] };
            },
            requirementDefinitions(): Record<string, TprmRequirementDefinition> {
                return useDashboardStore().tprmRequirementsDefinitions || {};
            },
            nonMultiInstanceRequirementTypes(): string[] {
                return (this as any).clientRequirementTypesStatus.list.filter(
                    (type: string) => !(this as any).requirementDefinitions[type]?.multiInstance,
                );
            },
            contractExtractionCountryCode(): string {
                return CONTRACT_EXTRACTION_COUNTRY_CODE;
            },
            contractExtractionRequiredFeatures(): RbacFeature[] {
                return [
                    RbacFeature.contractExtractionGetData,
                    RbacFeature.contractExtractionGetDocument,
                ];
            },
            userFeatures(): Partial<Record<RbacFeature, boolean>> {
                return useUserStore().currentRpRbacFeatures || {};
            },
            canViewContractExtractionData(): boolean {
                return Boolean((this as any).userFeatures[RbacFeature.contractExtractionGetData]);
            },
            canViewContractExtractionDocument(): boolean {
                return Boolean((this as any).userFeatures[RbacFeature.contractExtractionGetDocument]);
            },
            canManageContractExtractionRequirements(): boolean {
                return (this as any).canViewContractExtractionData;
            },
            loadingContractExtractionDocuments(): boolean {
                return (this as any).canViewContractExtractionDocument && (
                    (this as any).contractExtractionDocumentsStatus.status === OperationStatus.loading ||
                    (this as any).contractExtractionDocumentsStatus.status === OperationStatus.uninitialized
                );
            },
            loadingContractExtractionCriteria(): boolean {
                return (this as any).canViewContractExtractionData && (
                    (this as any).contractExtractionCriteriaStatus.status === OperationStatus.loading ||
                    (this as any).contractExtractionCriteriaStatus.status === OperationStatus.uninitialized
                );
            },
            loadingCriterionTemplates(): boolean {
                return (this as any).canViewContractExtractionData && (
                    (this as any).criterionTemplatesStatus.status === OperationStatus.loading ||
                    (this as any).criterionTemplatesStatus.status === OperationStatus.uninitialized
                );
            },
            loadingRequirementTypes(): boolean {
                return (this as any).canViewContractExtractionData && (
                    (this as any).requirementTypesPerCountryStatus.status === OperationStatus.loading ||
                    (this as any).requirementTypesPerCountryStatus.status === OperationStatus.uninitialized
                );
            },
            loadingRequirementModels(): boolean {
                return (this as any).canViewContractExtractionData && (
                    (this as any).requirementModelsStatus.status === OperationStatus.loading ||
                    (this as any).requirementModelsStatus.status === OperationStatus.uninitialized
                );
            },
            loadingCustomProperties(): boolean {
                return (this as any).canViewContractExtractionData && (
                    (this as any).customPropertiesStatus.status === OperationStatus.loading ||
                    (this as any).customPropertiesStatus.status === OperationStatus.uninitialized
                );
            },
            loadingClientRequirementTypes(): boolean {
                return (this as any).canViewContractExtractionData && (
                    (this as any).clientRequirementTypesStatus.status === OperationStatus.loading ||
                    (this as any).clientRequirementTypesStatus.status === OperationStatus.uninitialized
                );
            },
            displayedDocuments() {
                return (this as any).canViewContractExtractionDocument
                    ? ((this as any).contractExtractionDocumentsStatus.documents || [])
                    : [];
            },
            contractExtractionRequirementTypes(): TprmRequirementType[] {
                return (this as any).requirementTypesPerCountryStatus.data?.[CONTRACT_EXTRACTION_COUNTRY_CODE]
                    ?.coverageTypes || [];
            },
            contractExtractionRequirementModels(): TprmRequirementModel[] {
                return (this as any).requirementModelsStatus.list || [];
            },
            contractExtractionCriterionTemplates(): CriterionTemplate[] {
                return (this as any).criterionTemplatesStatus.list || [];
            },
            contractExtractionCustomProperties(): CustomProperty[] {
                return (this as any).customPropertiesStatus.list || [];
            },
            entityFormId(): string {
                return `contract-extraction-entity-${(this as any).actionDetails.contractExtractionId}`;
            },
            entityFormHasErrors(): boolean {
                if (!(this as any).submitAttempted || (this as any).isAssignToExisting) {
                    return false;
                }
                const form = useDynamicFormStore().forms[(this as any).entityFormId];
                return form ? Object.keys(form.formErrors || {}).length > 0 : false;
            },
            entitySearchHasError(): boolean {
                return (this as any).submitAttempted
                    && (this as any).isAssignToExisting
                    && !(this as any).selectedEntityId;
            },
            requirementsFormHasErrors(): boolean {
                if (!(this as any).submitAttempted) {
                    return false;
                }
                return (this as any).requirementsHasInvalidReferences;
            },
            documentsAnchorGap(): number {
                return DOCUMENTS_ANCHOR_GAP_PX;
            },
            pageTopOffset(): number {
                return PAGE_TOP_OFFSET_PX;
            },
            requirementsMinHeight(): string {
                return `calc(100vh - ${(this as any).documentsFixedTop}px - 21px)`;
            },
            isLoading(): boolean {
                return (this as any).loadingContractExtractionDocuments
                    || (this as any).loadingContractExtractionCriteria
                    || (this as any).loadingCriterionTemplates
                    || (this as any).loadingRequirementTypes
                    || (this as any).loadingRequirementModels
                    || (this as any).loadingCustomProperties
                    || (this as any).loadingClientRequirementTypes;
            },
        },
        watch: {
            'actionReview.action.contractExtractionId': {
                immediate: true,
                handler() {
                    void (this as any).loadContractExtractionDocument();
                    void (this as any).loadContractExtractionData();
                },
            },
            '$rp.current': {
                immediate: true,
                handler() {
                    void (this as any).loadContractExtractionDocument();
                    void (this as any).loadContractExtractionData();
                },
            },
            'canViewContractExtractionData': {
                immediate: true,
                handler(canView: boolean) {
                    if (canView) {
                        void (this as any).loadContractExtractionData();
                    }
                },
            },
            'canViewContractExtractionDocument': {
                immediate: true,
                handler(canView: boolean) {
                    if (canView) {
                        void (this as any).loadContractExtractionDocument();
                    }
                },
            },
            'isLoading': {
                immediate: true,
                handler(isLoading: boolean) {
                    if (!isLoading) {
                        this.$nextTick(() => {
                            (this as any).syncDocumentsAnchorTarget();
                        });
                    }
                },
            },
        },
        methods: {
            syncDocumentsAnchorTarget(): void {
                (this as any).documentsAnchorTarget =
                    ((this.$refs.actionHeader as Vue | undefined)?.$el as HTMLElement | undefined) || null;
            },
            async loadContractExtractionDocument(): Promise<void> {
                if (this.loadDocumentPromise) {
                    return this.loadDocumentPromise;
                }

                const rpName = (this as any).rpName;
                const contractExtractionId = (this as any).actionDetails.contractExtractionId;

                if (!rpName || !contractExtractionId || !(this as any).canViewContractExtractionDocument) {
                    return;
                }

                const promise = Promise.resolve(
                    useContractExtractionStore().loadContractExtractionDocuments({
                        rpName,
                        contractExtractionId,
                    }),
                ).finally(() => {
                    this.loadDocumentPromise = null;
                });
                this.loadDocumentPromise = promise;
                await promise;
            },
            async loadContractExtractionData(): Promise<void> {
                if (this.loadDataPromise) {
                    return this.loadDataPromise;
                }

                const rpName = (this as any).rpName;
                const contractExtractionId = (this as any).actionDetails.contractExtractionId;

                if (!rpName || !contractExtractionId || !(this as any).canViewContractExtractionData) {
                    return;
                }

                const promise = Promise.all([
                    useContractExtractionStore().loadContractExtractionCriteria({
                        rpName,
                        contractExtractionId,
                    }),
                    useDecisioningCriteriaStore().loadCriterionTemplates({
                        rpName,
                        countryCode: CONTRACT_EXTRACTION_COUNTRY_CODE,
                    }),
                    useDecisioningCriteriaStore().loadRequirementTypesPerCountry({ rpName }),
                    useDecisioningCriteriaStore().loadRequirementModels({ rpName }),
                    useDashboardStore().loadCustomProperties({ rpName }),
                    useDashboardStore().loadClientRequirementTypes({ rpName }),
                ]).finally(() => {
                    this.loadDataPromise = null;
                });
                this.loadDataPromise = promise;
                await promise;
            },
            onEntitySelect(entityId: string | null): void {
                (this as any).selectedEntityId = entityId;
            },
            onEntityModeChange(isAssignToExisting: boolean): void {
                (this as any).isAssignToExisting = isAssignToExisting;
            },
            onAcceptClick(): void {
                const formId = (this as any).entityFormId;

                if (!(this as any).submitAttempted) {
                    (this as any).submitAttempted = true;
                    useDynamicFormStore().setShowErrors({ formId, showError: true });
                }

                const form = useDynamicFormStore().forms[formId];

                if ((this as any).isAssignToExisting) {
                    if (!(this as any).selectedEntityId) {
                        return;
                    }
                } else {
                    const hasErrors = form && Object.keys(form.formErrors || {}).length > 0;
                    if (hasErrors) {
                        return;
                    }
                }

                const hasRequirementsErrors = (this as any).requirementsHasInvalidReferences;
                if (hasRequirementsErrors) {
                    return;
                }

                const editableCriteria: EditableRequirementCriterion[] =
                    this.contractExtractionCriteriaStatus.editableCriteria || [];
                const templates: CriterionTemplate[] = this.contractExtractionCriterionTemplates;
                const criteria = editableCriteria.map((criterion: EditableRequirementCriterion) => {
                    const template = templates.find((t: CriterionTemplate) =>
                        t.coverageType === criterion.coverageType && t.field === criterion.field,
                    );
                    const templateReferences: Record<string, CriterionField> = template?.evaluator.references || {};
                    return editableRequirementCriterionToRiskProfileCriterion(
                        criterion,
                        templateReferences,
                    );
                }) as CriterionInput[];

                const entity = (this as any).isAssignToExisting
                    ? { type: 'Existing' as const, entityId: (this as any).selectedEntityId }
                    : {
                        type: 'New' as const,
                        entity: convertContractEntityToApiModel(filterEntityEmptyItems(
                            trimEntity(buildEntityInputFromFormValues(form.values)),
                            this.contractExtractionCustomProperties,
                        ) as EntityInput),
                    };
                const evaluationRuleRequirementTypes: TprmRequirementType[] =
                    this.contractExtractionCriteriaStatus.editableEvaluationRuleRequirementTypes || [];
                const evaluationRule = evaluationRuleRequirementTypes.length > 0
                    ? { coverageTypes: evaluationRuleRequirementTypes }
                    : undefined;
                const resolve: ContractExtractionResolveInput = {
                    $action: ActionType.contractExtraction,
                    riskProfile: { entity, criteria, evaluationRule },
                };

                this.$emit('resolve', resolve);
            },
            onRejectClick(): void {
                (this as any).showRejectConfirmation = true;
            },
            onCancelClick(): void {
                (this as any).$emit('cancel');
            },
            onRejectConfirmation(value?: boolean): void {
                (this as any).showRejectConfirmation = false;
                if (value) {
                    const resolve: ContractExtractionResolveInput = {
                        $action: ActionType.contractExtraction,
                        riskProfile: null,
                    };
                    (this as any).$emit('resolve', resolve);
                }
            },
        },
    });
</script>
